import { cn } from '@brew/ui/cn'
import { PlaceholderIcon } from './placeholder-icon'

export type MediaAspect = '16/10' | '16/9' | '1/1' | '4/3'
export type MediaTone = 'cream' | 'ink'

export type MediaPlaceholderProps = {
  /** Which asset slot this is; also the frame's accessible name. */
  label: string
  /** What to drop in, e.g. a GIF or a video. */
  hint: string
  aspect?: MediaAspect
  tone?: MediaTone
  className?: string
}

const ASPECT_CLASS: Record<MediaAspect, string> = {
  '16/10': 'aspect-[16/10]',
  '16/9': 'aspect-[16/9]',
  '1/1': 'aspect-square',
  '4/3': 'aspect-[4/3]',
}

const TONE_CLASS: Record<MediaTone, string> = {
  cream: 'bg-background border-border text-muted-foreground dark:bg-card',
  ink: 'bg-foreground/[0.04] border-border text-foreground/60 dark:bg-foreground/[0.06] dark:border-border',
}

/**
 * Framed visual placeholder used everywhere the user will later drop in
 * a GIF / image / video. Renders an explicit label + hint so a human
 * reading the page knows exactly which asset slot they're looking at. The
 * caller passes both in the reader's language.
 *
 * No drop shadow (Cursor aesthetic). Frame is a single 1px border with
 * a rounded corner; inner dashed rectangle marks the asset bounds.
 */
export function MediaPlaceholder({
  label,
  hint,
  aspect = '16/10',
  tone = 'cream',
  className,
}: MediaPlaceholderProps) {
  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-2xl border',
        ASPECT_CLASS[aspect],
        TONE_CLASS[tone],
        className
      )}
      role="img"
      aria-label={label}
    >
      <div className="absolute inset-3 flex items-center justify-center rounded-xl border border-current/40 border-dashed">
        <div className="flex flex-col items-center gap-3 px-6 text-center">
          <PlaceholderIcon className="size-8 opacity-70" />
          <div className="flex flex-col gap-1">
            <span className="font-medium text-foreground/80 text-sm">
              {label}
            </span>
            <span className="text-current/80 text-xs">{hint}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
